import { lazy, ComponentType } from 'react';

/**
 * Checks if an error is a chunk loading error (happens after deployments when
 * cached HTML references old chunk files that no longer exist)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();
  
  // Common patterns for chunk loading errors
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to load module script') ||
    name.includes('chunkloaderror') ||
    // Vite-specific error patterns
    message.includes('unable to preload css') ||
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Session storage key to track reload attempts for chunk errors
 */
const CHUNK_RELOAD_KEY = 'deepfocus_chunk_reload_attempted';
const CHUNK_RELOAD_TIMESTAMP_KEY = 'deepfocus_chunk_reload_timestamp';
const MAX_RELOAD_AGE_MS = 30000; // 30 seconds - reset reload attempt after this time

/**
 * Checks if we've recently attempted a reload for chunk errors
 * This prevents infinite reload loops
 */
function hasRecentReloadAttempt(): boolean {
  try {
    const attempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const timestamp = sessionStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY);
    
    if (!attempted || !timestamp) return false;
    
    const age = Date.now() - parseInt(timestamp, 10);
    
    // If reload was attempted more than 30 seconds ago, allow another attempt
    if (age > MAX_RELOAD_AGE_MS) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      sessionStorage.removeItem(CHUNK_RELOAD_TIMESTAMP_KEY);
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}

/**
 * Marks that a reload attempt has been made
 */
function markReloadAttempt(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
    sessionStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clears the reload attempt marker (call this on successful page load)
 */
export function clearChunkReloadMarker(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    sessionStorage.removeItem(CHUNK_RELOAD_TIMESTAMP_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Handles a chunk loading error by reloading the page if we haven't recently tried
 * Returns true if a reload was triggered, false otherwise
 */
export function handleChunkError(): boolean {
  if (hasRecentReloadAttempt()) {
    console.warn('🔄 Chunk error detected but reload was recently attempted. Showing error UI.');
    return false;
  }
  
  console.log('🔄 Chunk loading error detected. Reloading page to get fresh assets...');
  markReloadAttempt();
  
  // Force a hard reload to bypass cache
  window.location.reload();
  return true;
}

/**
 * A wrapper around React.lazy that handles chunk loading errors gracefully.
 * If a chunk fails to load (common after deployments), it will:
 * 1. Retry loading the chunk once
 * 2. If retry fails, reload the page to get fresh assets
 * 3. If reload doesn't help, show an error to the user
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  componentName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const logPrefix = componentName ? `[${componentName}]` : '[LazyLoad]';
    
    try {
      // First attempt
      return await componentImport();
    } catch (error) {
      if (!isChunkLoadError(error)) {
        // Not a chunk loading error, re-throw immediately
        console.error(`${logPrefix} Error loading component:`, error);
        throw error;
      }
      
      console.warn(`${logPrefix} Chunk loading failed, retrying...`);
      
      // Wait a bit before retrying (in case it's a temporary network issue)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        // Second attempt
        return await componentImport();
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) {
          throw retryError;
        }
        
        console.error(`${logPrefix} Chunk loading failed after retry.`);
        
        // Try to reload the page to get fresh assets
        const reloaded = handleChunkError();
        
        if (!reloaded) {
          // If we've already tried reloading, throw a more user-friendly error
          const friendlyError = new Error(
            'A new version of DeepFocus is available. Please refresh the page to continue.'
          );
          friendlyError.name = 'ChunkLoadError';
          throw friendlyError;
        }
        
        // The page is reloading, so we'll never reach here,
        // but we need to return something for TypeScript
        return new Promise(() => {});
      }
    }
  });
}

/**
 * Call this on app initialization to clear the reload marker
 * This indicates the app loaded successfully
 */
export function initChunkErrorHandling(): void {
  // Clear reload marker on successful load
  clearChunkReloadMarker();
  
  // Add global handler for unhandled chunk errors (e.g., from code splitting)
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault();
      handleChunkError();
    }
  });
  
  // Handle promise rejections for dynamic imports
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      handleChunkError();
    }
  });
}

